/** Chart helpers shared by the primitives: colours (CSS variables with brand fallbacks), axis maths, container width. */
import { useEffect, useRef, useState } from 'react';

export interface ChartSeries {
    key: string;
    label: string;
    values: number[];
}

const COLORS = ['var(--chart-1, #0086a8)', 'var(--chart-2, #ec5800)', 'var(--chart-3, #6b7d00)', 'var(--chart-4, #5c5c5c)'] as const;
export const SURFACE = 'var(--bg-surface, #ffffff)';
export const GRID = 'var(--chart-grid, rgb(28 28 28 / 0.10))';
export const AXIS = 'var(--chart-axis, #5c5c5c)';
export const MUTED = 'var(--text-muted, #5c5c5c)';

export const colorOf = (i: number): string => COLORS[i % COLORS.length] ?? COLORS[0];

export const fillFor = (uid: string, i: number): string => (i % 4 === 0 ? colorOf(0) : `url(#${uid}-${i})`);

/** 1-2-5 "nice" axis ticks from 0 up to at least `max`. */
export const niceTicks = (max: number, count = 4): number[] => {
    if (!Number.isFinite(max) || max <= 0) return [0, 1];
    const raw = max / count;
    const pow = 10 ** Math.floor(Math.log10(raw));
    const f = raw / pow;
    const step = (f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10) * pow;
    const ticks: number[] = [];
    for (let v = 0; v < max + step * 0.999; v += step) ticks.push(Math.round(v / step) * step);
    return ticks;
};

/** Length of the longest y axis label for values up to `max`. */
export const tickChars = (max: number, format: (n: number) => string): number => Math.max(...niceTicks(max).map((v) => format(v).length));

export const clamp = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n));

/** Width of the referenced element, kept current with a ResizeObserver. `initial` is used for SSR/first paint. */
export const useWidth = (initial = 600) => {
    const ref = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(initial);
    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const read = () => {
            const w = Math.round(el.getBoundingClientRect().width);
            if (w > 0) setWidth(w);
        };
        read();
        const ro = new ResizeObserver(read);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    return { ref, width };
};

export const uidOf = (id: string): string => `c${id.replace(/[^a-zA-Z0-9]/g, '')}`;
